// App shell — sidebar + topbar + outlet for every authenticated page.
// Heavy lifting lives in /layout/ (helpers + hooks + subcomponents).

import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X, ShieldAlert, ShieldCheck, BarChart3 } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import { prewarmDrivePicker } from '@/components/integrations/DrivePickerButton';
import DriveStatusBadge from '@/components/integrations/DriveStatusBadge';
import BillingStatusBanner from '@/components/BillingStatusBanner';
import { ActiveCallLayer } from '@/components/meeting/ActiveCallLayer';

import { navItems } from './layout/navItems';
import { isModuleUnlocked } from './layout/helpers';
import { useScrollLockGuard } from '@/lib/useScrollLockGuard';
import { useSidebarCollapsed } from './layout/useSidebarCollapsed';
import { SidebarHeader } from './layout/SidebarHeader';
import { LockedNavItem, UnlockedNavItem } from './layout/SidebarNavItem';
import { SidebarFooter } from './layout/SidebarFooter';
// iter-156ao — ActiveGenerationsBanner + GenerationContext deleted;
// the legacy v1 AI generator that drove them is gone.
import { StrataTopBar } from './layout/StrataTopBar';
import TimezoneSetupBanner from './layout/TimezoneSetupBanner';
import { AddonLockedBanner } from './layout/AddonLockedBanner';
import { useRelayUnread } from '@/contexts/RelayUnreadContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform-admin only nav entries (rendered as a separate group at the
// bottom of the sidebar). Hidden unless `user.is_platform_admin` is true.
const adminNavItems = [
  { to: '/admin/companies', icon: ShieldAlert, label: 'Companies', testId: 'sidebar-admin-companies-link', title: 'Companies (Admin)' },
  { to: '/admin/usage', icon: BarChart3, label: 'Usage Analytics', testId: 'sidebar-admin-usage-link', title: 'Usage Analytics (Admin)' },
  { to: '/admin/security', icon: ShieldCheck, label: 'Login Security', testId: 'sidebar-admin-security-link', title: 'Login Security (Admin)' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { unread: relayUnread } = useRelayUnread();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  // iter-156sf — clear any orphaned Radix scroll-lock (Safari bfcache /
  // unmount-before-cleanup) so the page never gets stuck unscrollable.
  useScrollLockGuard();

  // One-time: fetch the company logo (also used as favicon) +
  // pre-warm the Google Drive Picker SDK so first-tap is instant.
  //
  // iter-156bq: switched from base64-in-JSON to binary at the API
  // layer (saves ~25% bytes, enables ETag/304 revalidation).
  // The Layout shell mounts once for the entire authenticated
  // session, so a single HEAD probe runs once. On every subsequent
  // page change the `<img src={url}>` is preserved (same component
  // tree) so there is no refetch.
  useEffect(() => {
    const url = `${API}/company/logo`;
    fetch(url, { method: 'HEAD', credentials: 'include' })
      .then((r) => {
        if (!r.ok || r.status === 204) return;
        setCompanyLogo(url);
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = url;
      })
      .catch(() => {});
    prewarmDrivePicker();
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex min-h-screen bg-background">
      <button
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md transition-colors"
        style={{
          background: 'var(--strata-sidebar)',
          border: '1px solid var(--strata-sidebar-border)',
          color: 'var(--strata-sidebar-text-strong)',
        }}
        onClick={() => setMobileOpen((v) => !v)}
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? '1' : '0'}
        className={`strata-sidebar fixed lg:sticky top-0 left-0 z-40 h-screen flex flex-col transition-[width,transform] duration-200 ease-out print:hidden ${
          collapsed ? 'w-[72px]' : 'w-64'
        } ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{
          background: 'var(--strata-sidebar)',
          borderRight: '1px solid var(--strata-sidebar-border)',
        }}
      >
        <SidebarHeader
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          user={user}
        />

        {/* iter-156ao — ActiveGenerationsBanner removed (dead since v1 generator nuke) */}

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {navItems
            .filter((item) => (item.anyPerm ? item.anyPerm.some((p) => can(p)) : can(item.perm)))
            .filter((item) => !(item.requiresAspire && !user?.aspire_connected))
            .filter((item) => !(item.requiresStrataEstimating && user?.estimating_mode !== 'strata'))
            .map((item) => {
              const unlocked = item.anyAddon
                ? item.anyAddon.some((a) => isModuleUnlocked(a, user?.integration_flags))
                : isModuleUnlocked(item.addon, user?.integration_flags);
              if (!unlocked && item.hideWhenLocked) return null;
              const navItem =
                item.to === '/relay' && relayUnread.total > 0
                  ? { ...item, badge: relayUnread.total > 99 ? '99+' : String(relayUnread.total) }
                  : item;
              return unlocked ? (
                <UnlockedNavItem
                  key={item.to} item={navItem}
                  collapsed={collapsed} onClick={closeMobile}
                />
              ) : (
                <LockedNavItem key={item.to} item={item} collapsed={collapsed} />
              );
            })}

          {/* Platform-admin only nav group. Hidden unless the current
              user has `is_platform_admin` (env-driven email allowlist). */}
          {user?.is_platform_admin && (
            <>
              <div
                className={`pt-3 pb-1 ${collapsed ? 'px-0 text-center' : 'px-3'} text-[9px] uppercase tracking-[0.18em] opacity-50`}
                style={{ color: 'var(--strata-sidebar-text)', fontFamily: 'var(--strata-font-body)' }}
              >
                {collapsed ? '·' : 'Platform admin'}
              </div>
              {adminNavItems.map((item) => (
                <UnlockedNavItem
                  key={item.to} item={item}
                  collapsed={collapsed} onClick={closeMobile}
                />
              ))}
            </>
          )}
        </nav>

        <SidebarFooter
          collapsed={collapsed}
          user={user}
          companyLogo={companyLogo}
          onLogout={logout}
        />
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={closeMobile}
        />
      )}

      <main className="flex-1 min-w-0 min-h-screen lg:ml-0 flex flex-col">
        <StrataTopBar />
        <AddonLockedBanner />
        <BillingStatusBanner />
        <TimezoneSetupBanner />
        <div className="p-6 pt-3 lg:p-8 lg:pt-4 w-full min-w-0 flex-1">
          <Outlet />
        </div>
      </main>

      {/* Floating Drive sync nudge — bottom-right. Drive is optional;
          this only surfaces when an already-connected Drive sync
          reports a real error or needs re-consent. */}
      <DriveStatusBadge />

      {/* Persistent meeting surface — an active call stays live across page
          navigation, shown full-screen or as a floating PiP. */}
      <ActiveCallLayer />
    </div>
  );
}
